// src/composables/useTheme.js
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

export const THEME_KEY = 'theme';

export const THEMES = {
  AUTO: 'auto',
  CLASSIC_LIGHT: 'classic-light',
  PURE_LIGHT: 'pure-light',
  DESERT_SAND: 'desert-sand',
  MODERN_DARK: 'modern-dark',
  DEEP_BLACK: 'deep-black',
  CYBER_NEON: 'cyber-neon',
};

// Order used when clicking "Next Theme"
const themeOrder = [
  THEMES.CLASSIC_LIGHT,
  THEMES.PURE_LIGHT,
  THEMES.DESERT_SAND,
  THEMES.MODERN_DARK,
  THEMES.DEEP_BLACK,
  THEMES.CYBER_NEON,
];

export function useTheme() {
  const currentStoredTheme = ref(localStorage.getItem(THEME_KEY) || THEMES.AUTO);

  // Compute effective theme when AUTO
  const getEffectiveTheme = () => {
    if (currentStoredTheme.value === THEMES.AUTO) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? THEMES.DEEP_BLACK
        : THEMES.CLASSIC_LIGHT;
    }
    return currentStoredTheme.value;
  };

  const currentTheme = ref(getEffectiveTheme());

  // Human-readable text for dropdown UI
  const displayName = computed(() => {
    const map = {
      [THEMES.AUTO]: 'Auto',
      [THEMES.CLASSIC_LIGHT]: 'Classic Light',
      [THEMES.PURE_LIGHT]: 'Pure Light',
      [THEMES.DESERT_SAND]: 'Desert Sand',
      [THEMES.MODERN_DARK]: 'Modern Dark',
      [THEMES.DEEP_BLACK]: 'Deep Black',
      [THEMES.CYBER_NEON]: 'Cyber Neon',
    };
    return map[currentStoredTheme.value] || currentStoredTheme.value;
  });

  const applyTheme = (theme) => {
    let effectiveTheme = theme;

    if (theme === THEMES.AUTO) {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? THEMES.DEEP_BLACK
        : THEMES.CLASSIC_LIGHT;

      localStorage.setItem(THEME_KEY, THEMES.AUTO);
      currentStoredTheme.value = THEMES.AUTO;
    } else {
      localStorage.setItem(THEME_KEY, theme);
      currentStoredTheme.value = theme;
    }

    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.body.setAttribute('data-theme', effectiveTheme);
    currentTheme.value = effectiveTheme;
  };

  const toggleTheme = () => {
    const current = getEffectiveTheme();
    const index = themeOrder.indexOf(current);
    const next = themeOrder[(index + 1) % themeOrder.length];
    applyTheme(next);
  };

  const handleSystemChange = () => {
    if (currentStoredTheme.value === THEMES.AUTO) {
      applyTheme(THEMES.AUTO);
    }
  };

  onMounted(() => {
    applyTheme(currentStoredTheme.value);
    const watcher = window.matchMedia('(prefers-color-scheme: dark)');
    watcher.addEventListener('change', handleSystemChange);
  });

  onBeforeUnmount(() => {
    const watcher = window.matchMedia('(prefers-color-scheme: dark)');
    watcher.removeEventListener('change', handleSystemChange);
  });

  return {
    currentTheme,
    currentStoredTheme,
    displayName,
    applyTheme,
    toggleTheme,
  };
}
